import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Box, 
    Table, 
    TableBody, 
    TableContainer, 
    TableRow, 
    Typography, 
    IconButton, 
    TextField,
    InputAdornment,
    Switch,
    TablePagination,
    Pagination,
    Card,
    Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';

import { useTableState } from '../../../../../hooks/useTableState';
import { corregirTexto, firstLetterUpperCase, hasPemissions, usarFuncionesComposicion } from '../../../../../commons/utils';
import { ModalConfirmDeactivation } from '../../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../../components/modals/ModalConfirmRestore';
import { StyledTableCell, StyledTableRow } from '../../../../../commons/generics/StyledTableComponents';
import { StatusChip } from '../../../../../commons/generics/StatusChip';
import { ClampedText } from '../../../../../commons/generics/ClampedText';
import { GenericTableHead, HeadCell } from '../../../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../../../commons/generics/SinDatos';

import { 
    getPlantillasStore, 
    getPlantillasByPagination, 
    getPlantillasBySearch, 
    storeDeletePlantilla, 
    storeRestorePlantilla 
} from '../../../../../store/ratings/thunks';

interface TablePlantillasProps {
    onEdit: (template: any) => void;
}

export const TablePlantillas = ({ onEdit }: TablePlantillasProps) => {
    const dispatch = useDispatch();
    const { plantillas_respuestas_list, google_bussinesses_list_for_select } = useSelector((state: any) => state.ratingsDevices);

    const {
        dataTableList,
        setCanView,
        canEdit, setCanEdit,
        canDelete, setCanDelete,
        canRestore, setCanRestore,
        openElem, setOpenElem,
        checked,
        searchTerm, setSearchTerm,
        order, orderBy,
        handleSwitchChange,
        handleSearch: handleSearchHook,
        handleResetSearch: handleResetSearchHook,
        page, rowsPerPage,
        handleChangePage, handleChangeRowsPerPage,
        pagePagination, itemCountPagination,
        changePaginationPage: changePaginationPageHook,
        handleRequestSort,
        updatePaginationFromList,
        openModalDeactivation, setOpenModalDeactivation,
        openModalRestore, setOpenModalRestore,
        ResponseComponents,
        handleResponse,
        getSortedAndFilteredList,
        isServerSide
    } = useTableState('plantillas_respuestas_table');

    const [hasPermission, setHasPermission] = useState<boolean>(true);
    const [inputValue, setInputValue] = useState('');
    const [selectedLocation, setSelectedLocation] = useState<any>(null);

    useEffect(() => {
        checkPermissions();
    }, []);

    useEffect(() => {
        getInitialData(selectedLocation?.value);
    }, [selectedLocation]);

    const getInitialData = async (locationId?: string) => {
        const response = await dispatch(getPlantillasStore(locationId));
        await handleResponse(response, 'obtener plantillas', false);
    };

    const checkPermissions = async () => {
        const view = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.reputacion.online', 'view', 'modulo.resenas.plantillas.respuestas');
        if (!view) setCanView(true);

        const edit = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.reputacion.online', 'edit', 'modulo.resenas.plantillas.respuestas');
        if (edit) setCanEdit(true);

        const del = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.reputacion.online', 'delete', 'modulo.resenas.plantillas.respuestas');
        if (del) setCanDelete(true);

        const rest = await hasPemissions('modulo.calificaciones', 'modulo.calificacion.reputacion.online', 'restore', 'modulo.resenas.plantillas.respuestas');
        if (rest) setCanRestore(true);
    };
 

    useEffect(() => {
        updatePaginationFromList(plantillas_respuestas_list);
    }, [plantillas_respuestas_list, updatePaginationFromList]);

    const handleSearch = async (param: string) => {
        setSearchTerm(param);
        setInputValue(param);
        handleSearchHook(param, dispatch, (p: string, page: number) => getPlantillasBySearch(p, page, selectedLocation?.value), () => getInitialData(selectedLocation?.value), null, 'buscar plantillas');
    };

    const onResetSearch = () => {
        setInputValue('');
        handleResetSearchHook(() => getInitialData(selectedLocation?.value));
    };

    const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
        changePaginationPageHook(event, value);
        const response = await dispatch(getPlantillasByPagination(value, selectedLocation?.value) as any);
        await handleResponse(response, 'listar plantillas', false);
    };

    const handleDelete = (row: any) => {
        setOpenElem(row);
        setOpenModalDeactivation(true);
    };

    const confirmDeactivate = async () => {
        if (!openElem) return;
        const response = await dispatch(storeDeletePlantilla(openElem.id, inputValue, pagePagination));
        await handleResponse(response, 'desactivar plantilla', true);
    };

    const handleRestore = (row: any) => {
        setOpenElem(row);
        setOpenModalRestore(true);
    };

    const confirmRestore = async () => {
        if (!openElem) return;
        const response = await dispatch(storeRestorePlantilla(openElem.id, inputValue, pagePagination));
        await handleResponse(response, 'restaurar plantilla', true);
    };

    if (!hasPermission) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error">No tienes permisos para ver este contenido.</Typography>
            </Box>
        );
    }

    const sortedList = getSortedAndFilteredList(
        dataTableList || [], 
        ['name', 'category', 'message', 'businessName'], 
        'status', 
        1
    );

    const headCells: HeadCell[] = [
        { id: 'name', label: 'Nombre', sortable: true },
        { id: 'businessName', label: 'Negocio', sortable: true },
        { id: 'category', label: 'Categoría', sortable: true },
        { id: 'message', label: 'Mensaje', sortable: true },
        { id: 'status', label: 'Estado', sortable: true },
        { id: 'actions', label: 'Acciones', sortable: false, align: 'center' },
    ];

    return (
        <Box sx={{ width: '100%', mt: 5 }}>
            {ResponseComponents}
            
            <ModalConfirmDeactivation 
                open={openModalDeactivation} 
                setOpen={setOpenModalDeactivation} 
                onConfirm={confirmDeactivate} 
            />
            <ModalConfirmRestore 
                open={openModalRestore} 
                setOpen={setOpenModalRestore} 
                onConfirm={confirmRestore} 
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Buscar plantilla..."
                        variant="outlined"
                        size="small"
                        autoComplete='off'
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        sx={{ width: 400 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={onResetSearch}><ClearIcon /></IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    
                    <Autocomplete
                        options={google_bussinesses_list_for_select || []}
                        getOptionLabel={(option: any) => option.label || ''}
                        value={selectedLocation}
                        onChange={(event, newValue) => {
                            setSelectedLocation(newValue);
                        }}
                        renderInput={(params) => <TextField {...params} label="Filtrar por Negocio" variant="outlined" size="small" />}
                        sx={{ width: 250 }}
                    />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Switch checked={checked} onChange={handleSwitchChange} color="primary" />
                    <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                        Mostrar Inactivas
                    </Typography>
                </Box>  
            </Box>

            <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                <Table>
                    <GenericTableHead
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                        {sortedList.length === 0 ? (
                            <TableRow>
                                <StyledTableCell colSpan={6} align="center">
                                    <SinDatos />
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            sortedList
                            .slice(isServerSide ? 0 : page * rowsPerPage, isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage)
                            .map((row: any) => (
                                <StyledTableRow key={row.id}>
                                    <StyledTableCell>
                                        <Typography variant="body2" fontWeight={600} color="text.primary">
                                            {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.name)}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.businessName || 'N/A')}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.category || 'N/A')}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <ClampedText text={usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, row.message || 'N/A')} lines={2} />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <StatusChip status={row.status} />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                            <span style={{ cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer' }}>
                                                <IconButton size="small" onClick={() => canEdit && row.status === 1 && onEdit(row)} disabled={!canEdit || row.status !== 1} color="primary">
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                            
                                            {row.status === 1 ? (
                                                <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                                    <IconButton size="small" onClick={() => canDelete && handleDelete(row)} disabled={!canDelete} color="error">
                                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            ) : (
                                                <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                                    <IconButton size="small" onClick={() => canRestore && handleRestore(row)} disabled={!canRestore} color="success">
                                                        <RestoreOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            )}
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                   {dataTableList.length > 0 && (
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={dataTableList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Filas por página"
                                />
                    )}

                        {dataTableList.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, padding: 2.5, borderTop: 1, borderColor: 'divider' }}>
                        <Pagination
                            sx={{ justifyContent: 'center' }}
                            color="primary"
                            count={itemCountPagination}
                            page={pagePagination}
                                onChange={changePaginationPage} 
                            showFirstButton 
                            showLastButton
                            shape="circular"
                        />
                    </Box>
                )}
                            
                </TableContainer>
            </Card>
        </Box>
    );
};
